import type { RuleExecutionKind } from '@/shared/api/resources';

/**
 * Rotulos e textos de pendencia da ficha do tomador.
 *
 * Cada pendencia aqui corresponde a um item de docs/OPEN_QUESTIONS.md, secao 2.16. A
 * tela mostra o campo e diz por que ele nao tem valor calculado, em vez de inventar
 * a conta.
 */

export const PENDENCIA_LIMITE_DISPONIVEL =
  'Pendente de regra: o consumo do limite geral (LIM-Q01 a LIM-Q08) nao esta aprovado, e o SeguraCore nao calcula o disponivel do limite geral. O disponivel por grupo de modalidades esta em Limites e Taxas.';

export const PENDENCIA_REPROCESSAMENTO =
  'O que o reprocessamento roda e regra sem fonte. Aqui so o motor de credito executa, pela execucao que ja existe; Pre-Cadastro, Grupo Economico e CCG registram "sem regra aprovada". Nada consulta o bureau.';

export const PENDENCIA_ASSINATURA_CCG =
  'No SeguraCore a CCG e gerada e assinada por operacao, na tela CCG. A CCG unica por tomador esta pendente de decisao.';

export const PENDENCIA_ASSINATURA_SEGURADORA =
  'Os responsaveis da seguradora pela assinatura da CCG e a dispensa dessa assinatura sao configuracao da empresa, ainda nao disponivel.';

export const PENDENCIA_GRUPO_26 =
  'Importante: a participacao deve ser validada a partir de 26% (CPO-077). A regra de aplicacao esta pendente; o percentual e registrado e nao decide nada.';

export const PENDENCIA_ANALISES_CORRETORA =
  'A analise de credito nao faz parte do acesso da corretora no SeguraCore. Se a corretora deve ver a situacao da analise e decisao pendente.';

export function rotuloSituacaoDeCredito(valor: string | undefined): string {
  if (valor === 'aprovado') return 'Aprovado';
  if (valor === 'reprovado') return 'Reprovado';
  return 'Nao decidido';
}

export const gruposDeModalidade: Record<string, string> = {
  tradicional: 'Tradicional',
  estruturadas: 'Estruturadas',
  judicial: 'Judicial',
  recursal: 'Recursal',
  financeira: 'Financeira',
  fianca: 'Fianca',
};

export const portesDeCredito = [
  { value: 'small', label: 'Small' },
  { value: 'small_plus', label: 'Small+' },
  { value: 'middle', label: 'Middle' },
  { value: 'middle_plus', label: 'Middle+' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'corporate_plus', label: 'Corporate+' },
];

export const classesDeRisco = ['AA', 'A', 'BB', 'B', 'CC', 'C', 'DD', 'D'].map(
  (classe) => ({
    value: classe,
    label: classe,
  }),
);

export const situacoesEspeciais = [
  { value: 'nenhuma', label: 'Nenhuma' },
  { value: 'recuperacao_judicial', label: 'Recuperacao judicial' },
  { value: 'recuperacao_extrajudicial', label: 'Recuperacao extrajudicial' },
  { value: 'falencia', label: 'Falencia' },
  { value: 'liquidacao', label: 'Liquidacao' },
  { value: 'intervencao', label: 'Intervencao' },
  { value: 'outra', label: 'Outra' },
];

export const papeisDaCcg: Record<string, string> = {
  representante_legal: 'Representante legal',
  fiador_pf: 'Fiador pessoa fisica',
  fiador_pj: 'Fiador pessoa juridica',
};

export const tiposDeCondicao: Record<string, string> = {
  premio_fixo: 'Premio fixo',
  premio_minimo: 'Premio minimo',
};

/** Nome legivel das acoes gravadas na trilha da ficha. */
export const acoesDaTrilha: Record<string, string> = {
  'tomadores.tomador.create': 'Cadastro do tomador',
  'tomadores.tomador.update': 'Alteracao do cadastro',
  'tomadores.tomador.block': 'Bloqueio',
  'tomadores.ccg.approve': 'Ignorar assinatura de CCG',
  'tomadores.restritivo.update': 'Bloqueio por restritivos',
  'tomadores.perfil_credito.update': 'Perfil de credito e restritivos',
  'tomadores.perfil_credito.preenchimento':
    'Perfil de credito preenchido pela consulta',
  'tomadores.filial.create': 'Inclusao de filial',
  'tomadores.filial.delete': 'Remocao de filial',
  'tomadores.dados_bancarios.create': 'Inclusao de conta bancaria',
  'tomadores.dados_bancarios.delete': 'Remocao de conta bancaria',
  'tomadores.observacao.create': 'Observacao',
  'tomadores.condicao.create': 'Inclusao de condicao comercial',
  'tomadores.condicao.update': 'Alteracao de condicao comercial',
  'tomadores.condicao.delete': 'Remocao de condicao comercial',
  'tomadores.ccg.create': 'Inclusao na CCG',
  'tomadores.ccg.delete': 'Remocao da CCG',
  'tomadores.ccg_contrato.update': 'Contrato de CCG',
  'tomadores.ccg_assinatura.create': 'Assinatura da CCG pedida',
  'tomadores.ccg_cancelamento.create': 'Cancelamento do contrato de CCG',
  'tomadores.socio.create': 'Inclusao no quadro societario',
  'tomadores.socio.update': 'Alteracao no quadro societario',
  'tomadores.socio.delete': 'Remocao do quadro societario',
  'tomadores.grupo.create': 'Inclusao em grupo economico',
  'tomadores.grupo.update': 'Alteracao de grupo economico',
  'tomadores.grupo.delete': 'Remocao de grupo economico',
  'tomadores.limite.update': 'Alteracao manual de limite e taxa',
  // P-TOM-C2: reprocessamento e limite calculado pelo motor.
  'tomadores.reprocessamento.create': 'Reprocessamento',
  'tomadores.limite.motor': 'Limite calculado pelo motor',
  // P-TOM-C1: listas do cadastro.
  'tomadores.nome_fantasia.create': 'Inclusao de nome fantasia',
  'tomadores.nome_fantasia.update': 'Alteracao de nome fantasia',
  'tomadores.nome_fantasia.delete': 'Remocao de nome fantasia',
  'tomadores.endereco.create': 'Inclusao de endereco',
  'tomadores.endereco.update': 'Alteracao de endereco',
  'tomadores.endereco.delete': 'Remocao de endereco',
  'tomadores.telefone.create': 'Inclusao de telefone',
  'tomadores.telefone.update': 'Alteracao de telefone',
  'tomadores.telefone.delete': 'Remocao de telefone',
  // P-TOM-C5: grupo economico como entidade e nomeacao do corretor.
  'tomadores.grupo_economico.update': 'Alteracao do grupo economico',
  'tomadores.grupo_economico_modalidade.update':
    'Limite do grupo por grupo de modalidades',
  'tomadores.nomeacao.create': 'Pedido de nomeacao',
  'tomadores.nomeacao_remocao.create': 'Pedido de remocao da nomeacao',
  'tomadores.nomeacao.approve': 'Aprovacao do pedido de nomeacao',
  'tomadores.nomeacao.decline': 'Recusa do pedido de nomeacao',
  'tomadores.nomeacao.cancel': 'Cancelamento do pedido de nomeacao',
};

export function rotuloDaAcao(acao: string): string {
  return acoesDaTrilha[acao] ?? acao;
}

export const situacoesDoCadastro = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

/** Permissao propria para mudar o limite de credito e a situacao do tomador. */
export const permissaoCredito = 'tomadores.credito.update';

/**
 * Dica mostrada a quem ve limite e situacao sem poder muda-los. O servidor recusa
 * a mudanca de qualquer forma; a dica diz por que o campo esta travado.
 */
export const dicaCredito =
  'Somente quem tem a permissao de alterar o credito do tomador muda o limite e a situacao (bloquear, desbloquear, ativar, inativar). Quem decide quem tem a permissao e a administracao.';

export const instrumentosDeNomeacao = [
  { value: 'primeira_emissao', label: 'Primeira emissao' },
  { value: 'ccg', label: 'CCG' },
  { value: 'carta_nomeacao', label: 'Carta de nomeacao' },
];

export const situacoesNaReceita = [
  { value: 'ativa', label: 'Ativa' },
  { value: 'suspensa', label: 'Suspensa' },
  { value: 'inapta', label: 'Inapta' },
  { value: 'baixada', label: 'Baixada' },
  { value: 'nula', label: 'Nula' },
];

export const situacoesDeCredito = [
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'reprovado', label: 'Reprovado' },
];

// --- Tomador complementar (P-TOM-C1) ------------------------------------------

/**
 * Funcionalidade que liga o tomador pessoa fisica por empresa. O OnPoint aceita CPF;
 * a decisao do dono esta aberta (ADR 0031), e a plataforma liga e desliga.
 */
export const FUNCIONALIDADE_PESSOA_FISICA = 'tomadores.pessoa_fisica';

export const tiposDePessoa = [
  { value: 'cnpj', label: 'Pessoa juridica (CNPJ)' },
  { value: 'cpf', label: 'Pessoa fisica (CPF)' },
];

export function rotuloDoTipoDePessoa(valor: string | undefined): string {
  return valor === 'cpf' ? 'Pessoa fisica' : 'Pessoa juridica';
}

export const PENDENCIA_VALORES_DO_GRUPO =
  'Concedido, utilizado e disponivel do grupo estao pendentes de definicao: dependem da metrica de consumo do grupo (LIM-005) e de como somar os tomadores, com ou sem a participacao (CRE-06), sem regra aprovada. Cada tomador do grupo mostra o aprovado e o utilizado dele. O limite do grupo e mostrado e nao bloqueia emissao nem consome nada: o teto do grupo e a alcada dele estao pendentes de regra (LIM-002, LIM-005 e G1).';

/** O que a tela mostra no lugar de concedido, utilizado e disponivel do grupo. */
export const TOTAL_DO_GRUPO_PENDENTE = 'pendente de definicao';

/**
 * Dica do grupo enquanto a lista de grupos de credito nao chega. A descricao vem do
 * cadastro dos grupos de modalidades (TOM-645 e TOM-122); sem ela, a dica mostra as
 * modalidades da empresa que estao no grupo.
 */
export const DICA_DO_GRUPO_INDISPONIVEL = 'A informacao do grupo nao foi carregada.';

// --- Tomador complementar: regras executadas e historico (P-TOM-C2) ------------

/** Permissao do reprocessamento, conforme a migration do pacote. */
export const permissaoReprocessamento = 'tomadores.reprocessamento.create';

/**
 * Os tipos de Regras Executadas do OnPoint, na ordem da tela dele (alfabetica), com
 * o que fica pendente em cada um.
 */
export const tiposDeRegra: { value: RuleExecutionKind; label: string }[] = [
  { value: 'ccg', label: 'CCG' },
  { value: 'grupo_economico', label: 'Grupo Economico' },
  { value: 'motor_credito', label: 'Motor de credito' },
  { value: 'pre_cadastro', label: 'Pre-Cadastro' },
];

export function rotuloDoTipoDeRegra(valor: string): string {
  return tiposDeRegra.find((tipo) => tipo.value === valor)?.label ?? valor;
}

export const situacoesDaExecucao: Record<string, string> = {
  executada: 'Executada',
  sem_regra_aprovada: 'Sem regra aprovada',
  nao_executada: 'Nao executada',
};

export const origensDaExecucao: Record<string, string> = {
  reprocessamento: 'Reprocessamento',
  operacao: 'Operacao',
  consulta: 'Depois da consulta ao bureau',
};

/** Resultado final do motor, como o motor o registra. */
export const resultadosDoMotor: Record<string, string> = {
  elegivel_aprovacao_automatica: 'Elegivel para aprovacao automatica',
  analise_manual: 'Analise manual',
  recusado: 'Recusado',
  pendente_documentacao: 'Pendente de documentacao',
  pendente_consulta: 'Pendente de consulta',
  pendente_aprovacao: 'Pendente de aprovacao',
  segue: 'Segue',
  nao_aplicavel: 'Nao aplicavel',
};

export function rotuloDoResultadoDoMotor(valor: string | undefined): string {
  if (!valor) return '';
  return resultadosDoMotor[valor] ?? valor;
}

/** Evento do historico de Limites e Taxas. */
export const eventosDoHistoricoDeLimites: Record<string, string> = {
  manual: 'Alteracao manual',
  motor: 'Calculo do motor de credito',
  carga: 'Carga de outro sistema',
};

export const PENDENCIA_LEITURA_DO_MOTOR =
  'Sucesso, fatal e alerta sao a leitura do resultado final do motor (so o elegivel e sucesso, so a recusa e fatal), a confirmar com a seguradora. Aprovacao manual por execucao esta pendente (CRD-015).';

export const PENDENCIA_LIMITE_DO_MOTOR =
  'Limite que o motor calculou na ultima execucao. O limite aprovado pela analise automatica vai para Aprovado; o aprovado por uma pessoa nunca e sobrescrito.';

// --- Grupo economico e nomeacao do corretor (P-TOM-C5) ------------------------

/** Permissoes do pacote, conforme a migration. */
export const permissaoLimiteDoGrupo = 'tomadores.grupo.update';
export const permissaoPedirNomeacao = 'tomadores.nomeacao.create';
export const permissaoDecidirNomeacao = 'tomadores.nomeacao.approve';
export const permissaoModoDaNomeacao = 'tomadores.nomeacao_parametros.update';

export const DICA_CORRETOR_POR_PEDIDO =
  'Nesta empresa o corretor muda por pedido de nomeacao, decidido pela seguradora, na secao Corretor do tomador.';

export const PENDENCIA_NOMEACAO =
  'A validade vencida nao encerra a nomeacao, e quem pode pedir com que carta nao e conferido: o efeito da nomeacao esta pendente de regra (CAD-016).';

export const modosDeNomeacao = [
  { value: 'pedido', label: 'Pedido de nomeacao, decidido pela seguradora' },
  { value: 'troca_direta', label: 'Troca direta do corretor no cadastro' },
];

export const tiposDeNomeacao: Record<string, string> = {
  pedido: 'Pedido de nomeacao',
  seguradora: 'Nomeacao pela seguradora',
  remocao: 'Pedido de remocao',
};

export const situacoesDaNomeacao: Record<string, string> = {
  pendente: 'Aguardando analise',
  aprovada: 'Aprovado',
  recusada: 'Recusado',
  cancelada: 'Cancelado',
};

export const fimDaNomeacao: Record<string, string> = {
  substituida: 'Substituida por outra nomeacao',
  removida: 'Removida',
};

export function rotuloDoInstrumento(valor: string): string {
  return instrumentosDeNomeacao.find((item) => item.value === valor)?.label ?? '';
}
